import { Card } from '@/components/ui'
import { formatCurrency, formatNumberRounded } from '@/lib/utils'
import { PlanData } from '@/services/analyticsService'

export interface PlanVsFactSectionProps {
  planData: PlanData
  marketingBudgetFact: number
  previousPeriodData?: PlanData | null
  currency?: string
}

export function PlanVsFactSection({
  planData,
  marketingBudgetFact,
  previousPeriodData,
  currency = 'RUB'
}: PlanVsFactSectionProps) {
  // Функции для цветового кодирования плана-факта
  const getMetricColor = (fact: number, plan: number) => {
    if (plan === 0) return 'text-gray-500'
    const percentage = (fact / plan) * 100
    if (percentage >= 100) return 'text-green-600'
    if (percentage >= 90) return 'text-yellow-600'
    if (percentage >= 70) return 'text-orange-600'
    return 'text-red-600'
  }

  const getMetricBgColor = (fact: number, plan: number) => {
    if (plan === 0) return 'bg-gray-50'
    const percentage = (fact / plan) * 100
    if (percentage >= 100) return 'bg-green-50'
    if (percentage >= 90) return 'bg-yellow-50'
    if (percentage >= 70) return 'bg-orange-50'
    return 'bg-red-50'
  }

  const getMetricPercentage = (fact: number, plan: number) => {
    if (plan === 0) return '—'
    const percentage = (fact / plan) * 100
    return `${percentage.toFixed(0)}%`
  }

  // Расчет экономики
  const marketingBudgetPlan = planData.marketing_budget_plan || 0
  const leadsCount = planData.leads_count_fact || 0
  const appointmentsCount = planData.appointments_count_fact || 0
  const visitorsCount = planData.new_visitors_count_fact || 0
  const salesCount = (planData.new_sales_count_fact || 0) + (planData.new_subscriptions_count_fact || 0)
  const planSalesCount = (planData.new_sales_count_plan || 0) + (planData.new_subscriptions_count_plan || 0)
  const newSalesAmount =
    (planData.new_sales_amount_fact || 0) +
    (planData.new_subscriptions_amount_fact || 0)

  const costPerLead = leadsCount > 0 ? marketingBudgetFact / leadsCount : 0
  const costPerAppointment = appointmentsCount > 0 ? marketingBudgetFact / appointmentsCount : 0
  const costPerVisit = visitorsCount > 0 ? marketingBudgetFact / visitorsCount : 0
  const costPerClient = salesCount > 0 ? marketingBudgetFact / salesCount : 0
  const avgClientValue = salesCount > 0 ? newSalesAmount / salesCount : 0
  const romi = costPerClient > 0 ? avgClientValue / costPerClient : 0

  // Рассчитываем цены для предыдущего периода
  let prevCostPerLead = 0
  let prevCostPerAppointment = 0
  let prevCostPerVisit = 0
  let prevCostPerClient = 0

  if (previousPeriodData) {
    const prevMarketingBudget = previousPeriodData.marketing_budget_fact || 0
    const prevLeads = previousPeriodData.leads_count_fact || 0
    const prevAppointments = previousPeriodData.appointments_count_fact || 0
    const prevVisitors = previousPeriodData.new_visitors_count_fact || 0
    const prevSales = (previousPeriodData.new_sales_count_fact || 0) + (previousPeriodData.new_subscriptions_count_fact || 0)

    prevCostPerLead = prevLeads > 0 ? prevMarketingBudget / prevLeads : 0
    prevCostPerAppointment = prevAppointments > 0 ? prevMarketingBudget / prevAppointments : 0
    prevCostPerVisit = prevVisitors > 0 ? prevMarketingBudget / prevVisitors : 0
    prevCostPerClient = prevSales > 0 ? prevMarketingBudget / prevSales : 0
  }

  // Функция для отображения изменения цены
  const renderCostChange = (currentCost: number, previousCost: number) => {
    if (!previousPeriodData || previousCost === 0) return null

    const change = ((currentCost - previousCost) / previousCost) * 100
    const isGood = change < 0 // снижение цены - хорошо

    return (
      <div
        className={`flex items-center gap-1 text-xs font-medium mt-0.5 ${
          isGood ? 'text-green-600' : 'text-red-600'
        }`}
      >
        <span>{isGood ? '↓' : '↑'}</span>
        <span>{Math.abs(change).toFixed(0)}%</span>
      </div>
    )
  }

  return (
    <Card>
      <h3 className="text-sm sm:text-base font-medium mb-3 sm:mb-4">План/факт и Экономика клиента</h3>

      {/* Две колонки на больших экранах, одна колонка на мобильных */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Левая колонка - План/Факт и Стоимости */}
        <div>
          {/* План/Факт - верхняя часть */}
          <div className="grid grid-cols-4 gap-1 sm:gap-2 lg:gap-3 mb-1">
            {/* Заявки */}
            <div
              className={`p-2 sm:p-3 rounded-t-lg ${getMetricBgColor(
                planData.leads_count_fact || 0,
                planData.leads_count_plan || 0
              )}`}
            >
              <p className="text-xs font-medium text-muted-foreground mb-2">Заявки</p>
              <p
                className={`text-sm sm:text-base lg:text-xl font-bold mb-1 ${getMetricColor(
                  planData.leads_count_fact || 0,
                  planData.leads_count_plan || 0
                )}`}
              >
                {formatNumberRounded(planData.leads_count_fact || 0)}/
                {formatNumberRounded(planData.leads_count_plan || 0)}
              </p>
              <p
                className={`text-xs font-semibold ${getMetricColor(
                  planData.leads_count_fact || 0,
                  planData.leads_count_plan || 0
                )}`}
              >
                {getMetricPercentage(planData.leads_count_fact || 0, planData.leads_count_plan || 0)}
              </p>
            </div>

            {/* Записи */}
            <div
              className={`p-2 sm:p-3 rounded-t-lg ${getMetricBgColor(
                planData.appointments_count_fact || 0,
                planData.appointments_count_plan || 0
              )}`}
            >
              <p className="text-xs font-medium text-muted-foreground mb-2">Записи</p>
              <p
                className={`text-sm sm:text-base lg:text-xl font-bold mb-1 ${getMetricColor(
                  planData.appointments_count_fact || 0,
                  planData.appointments_count_plan || 0
                )}`}
              >
                {formatNumberRounded(planData.appointments_count_fact || 0)}/
                {formatNumberRounded(planData.appointments_count_plan || 0)}
              </p>
              <p
                className={`text-xs font-semibold ${getMetricColor(
                  planData.appointments_count_fact || 0,
                  planData.appointments_count_plan || 0
                )}`}
              >
                {getMetricPercentage(
                  planData.appointments_count_fact || 0,
                  planData.appointments_count_plan || 0
                )}
              </p>
            </div>

            {/* Приходы */}
            <div
              className={`p-2 sm:p-3 rounded-t-lg ${getMetricBgColor(
                planData.new_visitors_count_fact || 0,
                planData.new_visitors_count_plan || 0
              )}`}
            >
              <p className="text-xs font-medium text-muted-foreground mb-2">Приходы</p>
              <p
                className={`text-sm sm:text-base lg:text-xl font-bold mb-1 ${getMetricColor(
                  planData.new_visitors_count_fact || 0,
                  planData.new_visitors_count_plan || 0
                )}`}
              >
                {formatNumberRounded(planData.new_visitors_count_fact || 0)}/
                {formatNumberRounded(planData.new_visitors_count_plan || 0)}
              </p>
              <p
                className={`text-xs font-semibold ${getMetricColor(
                  planData.new_visitors_count_fact || 0,
                  planData.new_visitors_count_plan || 0
                )}`}
              >
                {getMetricPercentage(
                  planData.new_visitors_count_fact || 0,
                  planData.new_visitors_count_plan || 0
                )}
              </p>
            </div>

            {/* Продажи */}
            <div
              className={`p-2 sm:p-3 rounded-t-lg ${getMetricBgColor(
                salesCount,
                planSalesCount
              )}`}
            >
              <p className="text-xs font-medium text-muted-foreground mb-2">Продажи</p>
              <p
                className={`text-sm sm:text-base lg:text-xl font-bold mb-1 ${getMetricColor(
                  salesCount,
                  planSalesCount
                )}`}
              >
                {formatNumberRounded(salesCount)}/
                {formatNumberRounded(planSalesCount)}
              </p>
              <p
                className={`text-xs font-semibold ${getMetricColor(
                  salesCount,
                  planSalesCount
                )}`}
              >
                {getMetricPercentage(salesCount, planSalesCount)}
              </p>
            </div>
          </div>

          {/* Стоимости под метриками */}
          <div className="grid grid-cols-4 gap-1 sm:gap-2 lg:gap-3">
            {/* Цена заявки */}
            <div
              className={`p-2 sm:p-3 rounded-b-lg border-t-2 border-gray-300 ${getMetricBgColor(
                planData.leads_count_fact || 0,
                planData.leads_count_plan || 0
              )}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm sm:text-base font-semibold text-gray-800">{formatCurrency(costPerLead, currency)}</p>
                {renderCostChange(costPerLead, prevCostPerLead)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Цена заявки</p>
            </div>

            {/* Цена записи */}
            <div
              className={`p-2 sm:p-3 rounded-b-lg border-t-2 border-gray-300 ${getMetricBgColor(
                planData.appointments_count_fact || 0,
                planData.appointments_count_plan || 0
              )}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm sm:text-base font-semibold text-gray-800">{formatCurrency(costPerAppointment, currency)}</p>
                {renderCostChange(costPerAppointment, prevCostPerAppointment)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Цена записи</p>
            </div>

            {/* Цена прихода */}
            <div
              className={`p-2 sm:p-3 rounded-b-lg border-t-2 border-gray-300 ${getMetricBgColor(
                planData.new_visitors_count_fact || 0,
                planData.new_visitors_count_plan || 0
              )}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm sm:text-base font-semibold text-gray-800">{formatCurrency(costPerVisit, currency)}</p>
                {renderCostChange(costPerVisit, prevCostPerVisit)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Цена прихода</p>
            </div>

            {/* Цена клиента */}
            <div
              className={`p-2 sm:p-3 rounded-b-lg border-t-2 border-gray-300 ${getMetricBgColor(
                salesCount,
                planData.new_sales_count_plan || 0
              )}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm sm:text-base font-semibold text-gray-800">{formatCurrency(costPerClient, currency)}</p>
                {renderCostChange(costPerClient, prevCostPerClient)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Цена клиента</p>
            </div>
          </div>
        </div>

        {/* Правая колонка - Маркетинговый бюджет и ROMI */}
        <div className="space-y-3">
          {/* Маркетинговый бюджет план/факт */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 sm:p-4">
            <p className="text-xs text-muted-foreground mb-1 sm:mb-2">Маркетинговый бюджет</p>
            <div className="flex items-baseline gap-2 mb-1">
              <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-800">{formatCurrency(marketingBudgetFact, currency)}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">/ {formatCurrency(marketingBudgetPlan, currency)}</p>
            </div>
          </div>

          {/* ROMI - связь между ценой клиента и клиент приносит */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Цена клиента</p>
                <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-800">{formatCurrency(costPerClient, currency)}</p>
              </div>
              <div className="flex flex-col items-center px-2 sm:px-4">
                <p className="text-xs text-muted-foreground mb-1">ROMI</p>
                <p
                  className={`text-xl sm:text-2xl font-bold ${
                    romi >= 3
                      ? 'text-green-600'
                      : romi >= 2
                      ? 'text-yellow-600'
                      : romi >= 1
                      ? 'text-orange-600'
                      : 'text-red-600'
                  }`}
                >
                  {romi.toFixed(2)}x
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">Клиент приносит</p>
                <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-800">{formatCurrency(avgClientValue, currency)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
